import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import PasswordInput from "@/components/PasswordInput";
import LegalFooter from "@/components/LegalFooter";

const CURRENT_TERMS_VERSION = "2026-02-01";
const OTHER_CITY = "__other__";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const next = sp.get("next");
  const safeNext = next && next.startsWith("/") && !next.startsWith("//") ? next : "/perfil";
  const { t } = useLang();
  const [cities, setCities] = useState([]);
  const [form, setForm] = useState({
    name: "", email: "", password: "", city: "", city_other: "",
    accepted_terms: false,
    age_confirmed: false,
    marketing_consent: false,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/cities").then(r => setCities(r.data.map(c => c.name))).catch(() => {}); }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isOther = form.city === OTHER_CITY;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.accepted_terms) return toast.error("Tens de aceitar os Termos e a Política de Privacidade.");
    if (!form.age_confirmed) return toast.error("Tens de confirmar que tens 18 anos ou mais.");
    setBusy(true);
    try {
      const { city_other, ...rest } = form;
      await register({ ...rest, city: isOther ? null : (form.city || null), city_other: isOther ? (city_other.trim() || null) : null, accepted_terms_version: CURRENT_TERMS_VERSION });
      toast.success(t("register_done_verify"));
      nav(safeNext);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erro ao criar conta");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#0d0d0d] text-white">
      <div className="auk-ribbon h-2" />
      <div className="flex-1 flex flex-col justify-center px-6 py-8 max-w-md mx-auto w-full">
        <Link to="/" className="text-xs text-white/60 mb-8 hover:text-white">← Voltar</Link>
        <h1 className="font-display text-3xl font-extrabold mb-1">Criar conta</h1>
        <p className="text-sm text-white/60 mb-8">Junta-te à comunidade angolana no Reino Unido.</p>

        <form onSubmit={submit} className="space-y-3">
          <input
            data-testid="register-name" name="given-name" autoComplete="given-name" required value={form.name} onChange={(e) => set("name", e.target.value)}
            placeholder="Primeiro nome"
            className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 outline-none focus:border-[#c8102e] text-white placeholder-white/40 transition-colors"
          />
          <input
            data-testid="register-email" name="email" autoComplete="email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)}
            placeholder="Email"
            className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 outline-none focus:border-[#c8102e] text-white placeholder-white/40 transition-colors"
          />
          <PasswordInput
            testid="register-password"
            required
            minLength={6}
            value={form.password}
            onChange={(e) => set("password", e.target.value)}
            placeholder="Palavra-passe (mín. 6 caracteres)"
            autoComplete="new-password"
          />
          <select
            data-testid="register-city" name="uk-city" value={form.city} onChange={(e) => set("city", e.target.value)}
            className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 outline-none focus:border-[#c8102e] text-white transition-colors [&>option]:text-black"
          >
            <option value="">Cidade no UK (opcional)</option>
            {cities.map(c => <option key={c} value={c}>{c}</option>)}
            <option value={OTHER_CITY}>A minha cidade não está na lista</option>
          </select>
          {isOther && (
            <input
              data-testid="register-city-other" autoComplete="off" maxLength={80}
              value={form.city_other} onChange={(e) => set("city_other", e.target.value)}
              placeholder="Indica a tua cidade/localidade (ex: Luton)"
              className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 outline-none focus:border-[#c8102e] text-white placeholder-white/40 transition-colors"
            />
          )}
          {isOther && <p className="text-[11px] text-white/50 leading-relaxed" data-testid="register-city-other-hint">Podes concluir o registo na mesma — ficas na Comunidade Geral AngolaUK+ e a tua localidade ajuda-nos a decidir as próximas comunidades locais.</p>}

          <div className="space-y-2 pt-2">
            <label className="flex items-start gap-2 text-xs leading-relaxed cursor-pointer">
              <input
                data-testid="register-age-checkbox"
                type="checkbox"
                checked={form.age_confirmed}
                onChange={(e) => set("age_confirmed", e.target.checked)}
                required
                className="mt-0.5 accent-[#c8102e] w-4 h-4 shrink-0"
              />
              <span className="text-white/80">
                Confirmo que tenho <strong className="text-white">18 anos ou mais</strong>.
              </span>
            </label>

            <label className="flex items-start gap-2 text-xs leading-relaxed cursor-pointer">
              <input
                data-testid="register-terms-checkbox"
                type="checkbox"
                checked={form.accepted_terms}
                onChange={(e) => set("accepted_terms", e.target.checked)}
                required
                className="mt-0.5 accent-[#c8102e] w-4 h-4 shrink-0"
              />
              <span className="text-white/80">
                Aceito os{" "}
                <Link to="/termos" target="_blank" className="text-[#ffd100] underline underline-offset-2">Termos de Uso</Link>.
              </span>
            </label>

            <label className="flex items-start gap-2 text-xs leading-relaxed cursor-pointer">
              <input
                data-testid="register-marketing-checkbox"
                type="checkbox"
                checked={form.marketing_consent}
                onChange={(e) => set("marketing_consent", e.target.checked)}
                className="mt-0.5 accent-[#c8102e] w-4 h-4 shrink-0"
              />
              <span className="text-white/70">
                Quero receber, ocasionalmente, novidades e destaques da comunidade por email (opcional).
              </span>
            </label>
          </div>

          <button
            data-testid="register-submit"
            type="submit" disabled={busy}
            className="w-full py-3 rounded-xl bg-[#c8102e] hover:bg-[#a50c24] disabled:opacity-50 font-semibold transition-colors mt-2"
          >
            {busy ? "A criar..." : t("register")}
          </button>
        </form>

        <p className="text-sm text-white/60 mt-6 text-center">
          Já tens conta? <Link to={next ? `/login?next=${encodeURIComponent(next)}` : "/login"} className="text-[#ffd100] font-semibold hover:underline">{t("login")}</Link>
        </p>

        <p className="text-[10px] text-white/40 mt-6 text-center leading-relaxed">
          A privacidade é importante. Só mostramos a tua cidade/região, nunca a morada exata.
        </p>

        <div className="mt-6"><LegalFooter variant="dark" /></div>
      </div>
    </div>
  );
}
